import { paymentStatementApi } from './base';

import type { DetectableRatePlanType, UploadConfig } from '../types';

// Export base API
export { paymentStatementApi, paymentStatementBaseQuery } from './base';

// Export teams API
export {
	teamsApi,
	useGetTeamMembersQuery,
	useInviteTeamMemberMutation,
	useCancelInviteMutation,
} from './teams';
export type {
	TeamMember,
	TeamResponse,
	InviteRequest,
	InviteResponse,
} from './teams';

// Export auth API
export {
	authApi,
	useLoginMutation,
	useRegisterMutation,
	useValidateTokenQuery,
	useGetUserInfoQuery,
	useGetAuthStatusQuery,
	useLogoutMutation,
	useVerifyEmailMutation,
	useForgotPasswordMutation,
	useResetPasswordMutation,
} from './auth';
export type {
	LoginRequest,
	RegisterRequest,
	UserData,
	AuthResponse,
	TokenValidationResponse,
	UserInfoResponse,
	AuthStatusResponse,
	ForgotPasswordRequest,
	ResetPasswordRequest,
} from './auth';

// Export tenants API
export {
	tenantsApi,
	useListTenantsQuery,
	useGetTenantConfigBySubdomainQuery,
	useGetTenantConfigByIdQuery,
	useGetTenantConfigQuery,
	useGetTenantQuery,
	useCreateTenantMutation,
	useUpdateTenantMutation,
	useDeleteTenantMutation,
} from './tenants';
export type {
	Tenant,
	TenantsResponse,
	TenantResponse,
	CreateTenantRequest,
	UpdateTenantRequest,
	ListTenantsParams,
} from './tenants';

// Export domains API
export {
	domainsApi,
	useGetTenantDomainsQuery,
	useGetVerificationInstructionsQuery,
	useVerifyDomainMutation,
} from './domains';
export type {
	Domain,
	DomainVerificationInstructions,
	DomainVerificationResult,
	DomainsResponse,
} from './domains';

// Re-export types for backward compatibility
export type { UploadConfig };

// Types for Statement Analyzer
export type StatementEngine = 'winstar' | 'platform';

/** ROC-72: verdict of the statement completeness/readability policy. */
export interface DocumentCheckIssue {
	code:
		| 'NOT_A_STATEMENT'
		| 'MISSING_PAGES'
		| 'UNREADABLE'
		| 'PARTLY_READABLE'
		| 'OTHER';
	reason: string;
	ui_message: string;
	email_message: string;
}

export interface DocumentCheck {
	status: 'ok' | 'flagged' | 'unknown';
	issues: DocumentCheckIssue[];
	policy_status?: string;
	confidence_score?: number | null;
	run_id?: string | null;
	checked_at?: string;
}

export interface StatementFile {
	id: string;
	filename: string;
	original_filename: string;
	file_size: number;
	statement_type: 'vendor_statement' | 'fee_schedule';
	analysis_type: 'cost_comparison' | 'fee_schedule_analysis';
	business_type:
		| 'restaurant'
		| 'retail'
		| 'ecommerce'
		| 'healthcare'
		| 'professional_services'
		| 'non_profit'
		| 'automotive'
		| 'education'
		| 'hospitality'
		| 'manufacturing'
		| 'other';
	current_status: 'uploaded' | 'processing' | 'processed' | 'error';
	created_at: string;
	updated_at: string;
	results?: any;
	uploaded_by?: { name: string; email: string };
	document_check?: DocumentCheck | null;
	/** A bounced upload (not a statement, missing pages, unreadable) waiting for a new file. */
	needs_new_file?: boolean;
	/** The statement's own month from its extracted data, e.g. "June 2026". */
	statement_month?: string | null;
	/** Set when the file is one month of a grouped merchant record. */
	merchant_record_id?: string | null;
	merchant_name?: string | null;
	rate_plan_type: string;
	// Extraction backend. Defaults to 'winstar' on legacy rows; 'platform'
	// rows route through the Deep Agent broker (statement_analyzer →
	// workflow_service in the platform namespace).
	engine?: StatementEngine;
	platform_run_id?: string | null;
	/** The referral partner, once statements carry one (ROC-117). */
	partner?: { id: string; name: string } | null;
}

export interface UploadStatementRequest {
	file: File;
	statement_type: 'vendor_statement' | 'fee_schedule';
	analysis_type: 'cost_comparison' | 'fee_schedule_analysis';
	business_type: string;
	uploadConfig?: UploadConfig;
}

export interface UploadStatementResponse {
	success: boolean;
	message: string;
	file_id: string;
	filename: string;
	s3_key: string;
	task_id: string;
}

export interface RerunStatementRequest {
	fileId: string;
	skipCache?: boolean;
	invalidateCache?: boolean;
	deleteResults?: boolean;
	// Rerun as this rate plan type instead of the originally selected one,
	// loading that type's default rate configuration (ROC-62 detected-type override)
	targetRatePlanType?: DetectableRatePlanType;
}

export interface StatementListParams {
	uploaded_by?: string;
	search?: string;
	profit_status?: 'positive' | 'negative' | '';
	engine?: StatementEngine;
	page?: number;
	page_size?: number;
}

export interface StatementListResponse {
	files: StatementFile[];
	pagination: {
		page: number;
		page_size: number;
		total: number;
		total_pages: number;
	};
}

export interface EnterpriseUser {
	id: string;
	email: string;
	name: string;
}

export interface EnterpriseUsersResponse {
	users: EnterpriseUser[];
}

export interface ProcessingStatus {
	status: string;
	logs: Array<{
		id: string;
		status: string;
		step_name: string;
		message: string;
		started_at: string;
		completed_at?: string;
		duration_seconds?: number;
		error_message?: string;
		step_data?: any;
	}>;
}

export interface DefaultRatesResponse {
	custom_rates: Record<string, any>;
	description: string;
	is_default: boolean;
	name: string;
}

export interface DetailedDocument {
	id: string;
	filename: string;
	original_filename: string;
	file_size: number;
	statement_type: string;
	analysis_type: string;
	action_type?: string; // Action type (upload, rerun, recalculation)
	business_type: string;
	current_status: string;
	created_at: string;
	updated_at: string;
	results?: any;
	signed_urls: {
		pdf_url?: string;
		data_url?: string;
		original_url?: string;
		original_content_type?: string;
		original_filename?: string;
		pdf_status?: 'ready' | 'preparing' | 'failed';
	};
	rate_plan_type: string;
	// Statement type the platform classifier detected (may differ from the
	// user-selected rate_plan_type); null until classification has run
	detected_rate_plan_type?: DetectableRatePlanType | null;
	/** The earlier credit was given back, so the next rerun or upload uses a report. */
	rerun_uses_report?: boolean;
	/** A different statement in its place (a replace, or a whole statement sent as pages) uses a report. */
	replace_uses_report?: boolean;
}

export interface ProcessingLogs {
	file_id: string;
	current_status: string;
	/** This run's document check, as soon as the run has one (ROC-116). */
	document_check?: DocumentCheck | null;
	logs: Array<{
		id: string;
		step_name: string;
		status: string;
		message: string;
		error_message?: string;
		created_at: string;
		started_at?: string;
		completed_at?: string;
		duration_seconds?: number;
		step_data?: any;
	}>;
}

/** One row of the processing log; the single contract every consumer should type against. */
export type ProcessingLogEntry = ProcessingLogs['logs'][number];

/** Backend `ProcessingStatusEnum` (python/statement_analyzer/schemas/enums.py). */
export type StatementProcessingStatus =
	| 'uploaded'
	| 'processing'
	| 'completed'
	| 'failed'
	| 'cancelled';

export type CardMixKey =
	| 'credit'
	| 'signature_debit'
	| 'pin_debit'
	| 'regulated_debit'
	| 'amex';

/** Volume, count and card mix a quote is priced on (ROC-97). */
export interface PricingAssumptions {
	monthly_volume: number;
	transaction_count: number;
	card_mix: Record<CardMixKey, number>;
}

export interface PricingAssumptionsResponse {
	success: boolean;
	/** The statement's own figures; null when the statement cannot take assumptions. */
	statement: PricingAssumptions | null;
	/** The edited figures the quote uses now; null means the statement's. */
	current: PricingAssumptions | null;
}

export interface DeleteResponse {
	success: boolean;
	message: string;
	file_id: string;
	deleted_from_s3: boolean;
	deleted_from_db: boolean;
}

// Line Item Types
export interface FeeType {
	id: string;
	code: string;
	name: string;
	category?: string;
}

export interface Processor {
	id: string;
	code: string;
	name: string;
}

export interface Network {
	id: string;
	code: string;
	name: string;
}

export interface LineItem {
	id: string;
	statement_id: string;
	statement_file_id?: string;
	line_number: number;
	description: string;
	amount: number;
	fee_type?: FeeType;
	processor?: Processor;
	network?: Network;
	extra_attributes?: Record<string, any>;
	created_at: string;
	updated_at: string;
	feedback_response?: FeedbackItem;
	/** ROC-105: extraction confidence 0-1; below the threshold a person checks it. */
	confidence_score?: number | null;
	needs_review?: boolean;
}

// Calculated totals from backend (statement-type-aware)
export interface ProcessorTotals {
	grand_total: number;
	effective_rate: number;
	volume: number;
}

export interface SavingsTotals {
	monthly: number;
	yearly: number;
	percent_lower: number;
	is_negative: boolean;
}

export interface CalculatedTotals {
	current_processor: ProcessorTotals;
	comparison_processor: ProcessorTotals;
	savings: SavingsTotals;
	calculation_method: string; // IC_PLUS, TIERED, FLAT_TIERED
	show_rate_disclaimer?: boolean; // True for FLAT_TIERED - rates are aggregate averages
	document_total?: {
		amount: number;
		label?: string | null;
		page_number?: number | null;
	} | null;
	calculated_total?: number | null;
}

// Revenue analysis from backend (ISO profit calculation)
// TODO: Standardize backend revenue response structure between IC_PLUS and FLAT_TIERED
// Currently, FLAT_TIERED returns Clay's formula fields (gross_profit, buy_rate_cost, etc.)
// while IC_PLUS returns margin-based fields (network_data with proposed rates).
// Future improvement: unify the response structure so both return the same fields,
// with calculation logic differing internally. This would simplify the frontend and
// eliminate the need for conditional rendering based on statement type.
// See: python/statement_analyzer/services/totals_calculator.py for backend logic
export interface RevenueBreakdown {
	volume_margin: number;
	auth_margin: number;
	monthly_margin: number;
	batch_margin?: number;
	details: {
		// Common fields
		monthly_buy_costs: number;
		// IC_PLUS/TIERED specific fields
		discount_buy_rate_vmd?: number;
		discount_buy_rate_amex?: number;
		auth_buy_rate_standard?: number;
		auth_buy_rate_pin_debit?: number;
		auth_buy_rate_ebt?: number;
		batch_fee_rate?: number;
		discount_buy_rate?: number;
		auth_buy_rate?: number;
		total_volume?: number;
		total_transaction_count?: number;
		network_data?: Record<
			string,
			{
				volume: number;
				transaction_count: number;
				proposed_discount_rate?: number;
				proposed_auth_fee?: number;
			}
		>;
		// FLAT_TIERED specific fields (Clay's formula)
		business_type?: string;
		gross_revenue?: number;
		total_income?: number;
		proposed_fees?: number;
		hard_costs?: number;
		interchange_cost?: number;
		card_brand_cost?: number;
		buy_rate_cost?: number;
		total_cost?: number;
		buy_rate_discount_cost?: number;
		buy_rate_auth_cost?: number;
		buy_rate_monthly_cost?: number;
		gross_profit?: number;
		total_profit?: number;
		network_breakdown?: Record<
			string,
			{
				volume: number;
				transaction_count: number;
				discount_buy_cost: number;
				auth_buy_cost: number;
			}
		>;
	};
}

export interface RevenueAnalysis {
	total_revenue: number;
	agent_share: number;
	house_share: number;
	agent_share_percent: number;
	risk_tier?: string;
	breakdown: RevenueBreakdown;
}

export interface LineItemsListResponse {
	success: boolean;
	line_items: LineItem[];
	total: number;
	statement_id?: string;
	calculated_totals?: CalculatedTotals;
	revenue_analysis?: RevenueAnalysis;
	custom_rates?: any; // Custom rates configuration used for this statement's calculation
	rate_plan_type?: string; // Statement's rate plan type (IC_PLUS, TIERED, FLAT_TIERED)
	statement_needs_check?: boolean; // ROC-105: partly readable, figures not checked yet
	figures_checked?: { by: string; at: string } | null;
}

export interface LineItemDetailResponse {
	success: boolean;
	message: string;
	line_item: LineItem;
}

export interface LineItemUpdateRequest {
	description?: string;
	amount?: number; // Fee Due
	fee_type_id?: string;
	processor_id?: string;
	network_id?: string;
	rate?: number; // Rate percentage (e.g., 0.0014 for 0.14%)
	per_transaction_fee?: number; // Fee (per transaction fee)
	volume?: number; // Volume amount
	parent_id?: string | null; // Parent line item ID for drag-drop category moves
	transaction_count?: number; // Number of transactions (Count)
	action?: string; // Action type: 'undo_move' to restore original position
	processor_type?: string; // 'ic' for current processor, 'icplus' for comparison processor
}

// Move warning response from backend
export interface MoveWarningResponse {
	code: string;
	message: string;
}

export interface LineItemUpdateResponse {
	success: boolean;
	message: string;
	line_item: LineItem;
	warnings?: MoveWarningResponse[];
}

export interface LineItemAuditLog {
	id: string;
	line_item_id: string;
	user_id: string;
	changes: Record<string, any>;
	old_values: Record<string, any>;
	action: string;
	created_at: string;
}

export interface LineItemAuditLogsResponse {
	success: boolean;
	audit_logs: LineItemAuditLog[];
	total: number;
}

export interface BulkLineItemUpdate {
	// Simplified move API fields
	current_item_id?: string; // ID of the item being moved (for move operations)
	target_item_id?: string | null; // ID of target parent (if moving to be a child)
	target_section?: string; // Target section name (if moving to section as root)

	// Legacy fields for non-move updates
	line_item_id?: string; // Line item ID to update (legacy - use current_item_id for moves)
	parent_id?: string | null; // New parent line item ID (legacy - use target_item_id for moves)
	section?: string; // Category section name (legacy - use target_section for moves)
	description?: string;
	amount?: number;
	fee_type_id?: string;
	processor_id?: string;
	network_id?: string;
	rate?: number;
	per_transaction_fee?: number;
	volume?: number;
	transaction_count?: number;
	display_order?: number;
}

export interface BulkLineItemUpdateRequest {
	updates: BulkLineItemUpdate[];
}

export interface BulkLineItemUpdateResponse {
	success: boolean;
	message: string;
	updated_count: number;
	failed_count: number;
	line_items: LineItem[];
	errors: Array<{ line_item_id: string; error: string }>;
	warnings?: MoveWarningResponse[];
}

export interface FeeTypesListResponse {
	success: boolean;
	fee_types: FeeType[];
	total: number;
}

export interface ProcessorsListResponse {
	success: boolean;
	processors: Processor[];
	total: number;
}

export interface NetworksListResponse {
	success: boolean;
	networks: Network[];
	total: number;
}

// Feedback Types
export interface FeedbackRequest {
	file_id: string;
	line_item_id: string;
	rating_type: 'THUMBS_UP' | 'THUMBS_DOWN';
	reason_code?: string;
	note?: string;
}

export interface FeedbackResponse {
	success: boolean;
	message: string;
	feedback_id?: string;
}

export interface FeedbackItem {
	id: string;
	file_id: string;
	line_item_id: string;
	rating_type: 'THUMBS_UP' | 'THUMBS_DOWN';
	reason_code?: string;
	note?: string;
	created_at: string;
	updated_at: string;
}

export interface FeedbackListResponse {
	success: boolean;
	message: string;
	data: FeedbackItem[];
	total: number;
	page: number;
	page_size: number;
}

export interface SubscriptionStatusResponse {
	available_billing_models: string[];
	billing_model: string;
	can_upload: boolean;
	credits_remaining: number;
	has_subscription: boolean;
	subscription_status: string;
	usage_limit: number | null;
	usage_this_period: number;
	credits_total: number;
	// Onboarding fields
	should_show_onboarding: boolean;
	onboarding_completed: boolean;
	onboarding_completed_at?: string;
	enterprise_id: string;
	user_roles: string[];
}

// Subscription API Types
export interface Invoice {
	id: number;
	customer: string;
	amount_due: number;
	amount_paid: number;
	status: 'paid' | 'pending' | 'failed';
	created: number; // Unix timestamp
	period_start?: number; // Unix timestamp
	period_end?: number; // Unix timestamp
	subtotal: number;
	tax: number;
	total: number;
	invoice_pdf?: string | null;
	hosted_invoice_url?: string | null;
	lines: Array<{
		description: string;
		quantity: number;
		unit_amount: number;
		amount: number;
		type: string;
	}>;
	promo_code_used?: string | null;
	promo_discount_amount: number;
	usage_details?: any;
}

export interface InvoicesResponse {
	invoices: Invoice[];
}

export interface InvoiceDetailsResponse {
	invoice: Invoice;
}

export interface PromoCodeValidationRequest {
	promo_code: string;
}

export interface PromoCodeValidationResponse {
	valid: boolean;
	message: string;
	promo_code: {
		id: number;
		code: string;
		name: string;
		description?: string;
		promoType: 'percentage' | 'fixed_amount' | 'free_credits';
		discountValue: number;
		isValid: boolean;
	};
}

export interface PurchaseCreditsRequest {
	quantity: number;
	description: string;
	promo_code?: string;
	user_type?: string;
}

export interface PurchaseCreditsResponse {
	success: boolean;
	checkout_url: string;
	message?: string;
}

export interface SetupMeteredRequest {
	promo_code?: string;
}

export interface SetupMeteredResponse {
	success: boolean;
	checkout_url: string;
	message?: string;
}

export interface BillingConfigResponse {
	show_billing_toggle: boolean;
	default_billing_model: 'credit_based' | 'metered_usage';
}

export interface PaymentStatusResponse {
	payment_status: 'paid' | 'pending' | 'failed';
	credits_added?: number;
	message?: string;
}

// Custom Rates Types - Updated to match actual API structure
export interface CustomRates {
	VISA?: {
		common?: {
			discount_rate?: number;
			authorization_fee?: number;
			dues_and_assessments?: number;
			card_brand_fee_apf_credit?: number;
			card_brand_fee_file_transmission?: number;
		};
		tiered?: {
			with_debit?: {
				tiered_rates_qualified_rate?: number;
				tiered_rates_qualified_per_item?: number;
				tiered_rates_midqualified_rate?: number;
				tiered_rates_midqualified_per_item?: number;
				tiered_rates_nonqualified_rate?: number;
				tiered_rates_nonqualified_per_item?: number;
				tiered_rates_rate?: number;
				tiered_rates_per_item?: number;
			};
			credit_only?: {
				tiered_rates_qualified_rate?: number;
				tiered_rates_qualified_per_item?: number;
				tiered_rates_midqualified_rate?: number;
				tiered_rates_midqualified_per_item?: number;
				tiered_rates_nonqualified_rate?: number;
				tiered_rates_nonqualified_per_item?: number;
				tiered_rates_rate?: number;
				tiered_rates_per_item?: number;
			};
		};
	};
	MASTERCARD?: {
		common?: {
			discount_rate?: number;
			authorization_fee?: number;
			dues_and_assessments?: number;
			card_brand_fee_nabu?: number;
			card_brand_fee_file_transmission?: number;
		};
		tiered?: {
			with_debit?: {
				tiered_rates_qualified_rate?: number;
				tiered_rates_qualified_per_item?: number;
				tiered_rates_midqualified_rate?: number;
				tiered_rates_midqualified_per_item?: number;
				tiered_rates_nonqualified_rate?: number;
				tiered_rates_nonqualified_per_item?: number;
				tiered_rates_rate?: number;
				tiered_rates_per_item?: number;
			};
			credit_only?: {
				tiered_rates_qualified_rate?: number;
				tiered_rates_qualified_per_item?: number;
				tiered_rates_midqualified_rate?: number;
				tiered_rates_midqualified_per_item?: number;
				tiered_rates_nonqualified_rate?: number;
				tiered_rates_nonqualified_per_item?: number;
				tiered_rates_rate?: number;
				tiered_rates_per_item?: number;
			};
		};
	};
	DISCOVER?: {
		common?: {
			discount_rate?: number;
			authorization_fee?: number;
			dues_and_assessments?: number;
			card_brand_fee_network_auth?: number;
			card_brand_fee_data_usage?: number;
		};
		tiered?: {
			with_debit?: {
				tiered_rates_qualified_rate?: number;
				tiered_rates_qualified_per_item?: number;
				tiered_rates_midqualified_rate?: number;
				tiered_rates_midqualified_per_item?: number;
				tiered_rates_nonqualified_rate?: number;
				tiered_rates_nonqualified_per_item?: number;
				tiered_rates_rate?: number;
				tiered_rates_per_item?: number;
			};
			credit_only?: {
				tiered_rates_qualified_rate?: number;
				tiered_rates_qualified_per_item?: number;
				tiered_rates_midqualified_rate?: number;
				tiered_rates_midqualified_per_item?: number;
				tiered_rates_nonqualified_rate?: number;
				tiered_rates_nonqualified_per_item?: number;
				tiered_rates_rate?: number;
				tiered_rates_per_item?: number;
			};
		};
	};
	// Additional card types
	VISA_DEBIT?: any;
	MASTERCARD_DEBIT?: any;
	DISCOVER_DEBIT?: any;
	PIN_DEBIT?: any;
	EBT?: any;
	FLEET?: any;
	[key: string]: any; // Allow for additional dynamic card types
}

export interface MonthlyFee {
	label: string;
	amount: number;
}

export interface MonthlyAndBatchFeesConfig {
	monthly_fees?: MonthlyFee[];
	batch_fee?: number;
	batch_count_per_month?: number;
}

export interface CustomRatesConfig {
	id?: string;
	name: string;
	description: string;
	is_default: boolean;
	rate_plan_type: 'TIERED' | 'IC_PLUS' | 'FLAT';
	selected_rate_types?: ('TIERED' | 'IC_PLUS' | 'FLAT_TIERED')[];
	partner_id?: string | null;
	risk_tier?: string | null;
	status?: string | null;
	custom_rates: CustomRates;
	created_at?: string;
	updated_at?: string;
	created_by?: string;
	updated_by?: string;
	enterprise_id?: string;
	preference_data?: CustomRates;
	preference_type?: 'custom_rates';
}

export interface CustomRatesResponse {
	success: boolean;
	message: string;
	timestamp: string;
	data: CustomRatesConfig;
}

export interface CustomRatesListResponse {
	success: boolean;
	message: string;
	data: CustomRatesConfig[];
	total: number;
	timestamp: string;
}

// Enterprise Settings Types
export interface AddressData {
	street?: string;
	city?: string;
	state?: string;
	zip?: string;
	country?: string;
}

export interface ContactData {
	phone?: string;
	email?: string;
	website?: string;
}

export interface EnterpriseSettingsResponse {
	id: string;
	enterprise_id: string;
	company_name: string;
	logo_base64?: string;
	address: AddressData;
	contact: ContactData;
	created_at: string;
	updated_at: string;
}

export interface CreateEnterpriseSettingsRequest {
	company_name: string;
	address?: AddressData;
	contact?: ContactData;
}

export interface UpdateEnterpriseSettingsRequest {
	company_name?: string;
	address?: AddressData;
	contact?: ContactData;
}

// Revenue Configuration Types
export type RiskTier = 'LOW' | 'MEDIUM' | 'HIGH' | 'OTHER';

export interface MonthlyFeeCost {
	label: string;
	amount: number;
}

export interface RevenueConfiguration {
	id: string;
	enterprise_id: string;
	name: string;
	description?: string;
	is_active: boolean;
	risk_tier: RiskTier;
	partner_revenue_share_percent?: number | null;
	// Legacy single rates
	discount_buy_rate: number;
	authorization_buy_rate: number;
	// NEW: Split discount rates
	discount_buy_rate_vmd?: number;
	discount_buy_rate_amex?: number;
	// NEW: Split authorization fees
	auth_buy_rate_standard?: number;
	auth_buy_rate_pin_debit?: number;
	auth_buy_rate_ebt?: number;
	// NEW: Batch fee
	batch_fee_rate: number;
	// NEW: Dynamic monthly fees
	monthly_fee_costs: MonthlyFeeCost[];
	// Legacy monthly fees
	monthly_fee_cost_1: number;
	monthly_fee_cost_1_label?: string;
	monthly_fee_cost_2: number;
	monthly_fee_cost_2_label?: string;
	monthly_fee_cost_3: number;
	monthly_fee_cost_3_label?: string;
	agent_revenue_share_percent: number;
	created_by: string;
	updated_by: string;
	created_at: string;
	updated_at: string;
}

export interface RevenueConfigTierInput {
	risk_tier: RiskTier;
	// Legacy single rates (required for backward compatibility)
	discount_buy_rate: number;
	authorization_buy_rate: number;
	// NEW: Split discount rates (optional, fallback to legacy if not provided)
	discount_buy_rate_vmd?: number;
	discount_buy_rate_amex?: number;
	// NEW: Split authorization fees (optional, fallback to legacy if not provided)
	auth_buy_rate_standard?: number;
	auth_buy_rate_pin_debit?: number;
	auth_buy_rate_ebt?: number;
	// NEW: Batch fee
	batch_fee_rate?: number;
	// NEW: Dynamic monthly fees
	monthly_fee_costs?: MonthlyFeeCost[];
	// Legacy monthly fees
	monthly_fee_cost_1?: number;
	monthly_fee_cost_1_label?: string;
	monthly_fee_cost_2?: number;
	monthly_fee_cost_2_label?: string;
	monthly_fee_cost_3?: number;
	monthly_fee_cost_3_label?: string;
	agent_revenue_share_percent: number;
}

export interface CreateRevenueConfigRequest {
	name: string;
	description?: string;
	is_active?: boolean;
	// Sent only when the partner changes; null unlinks it (partners.revenue_config_name).
	partner_id?: string | null;
	partner_revenue_share_percent?: number | null;
	configurations: RevenueConfigTierInput[];
}

export interface UpdateRevenueConfigRequest {
	name: string;
	description?: string;
	is_active?: boolean;
	// Sent only when the partner changes; null unlinks it (partners.revenue_config_name).
	partner_id?: string | null;
	partner_revenue_share_percent?: number | null;
	configurations?: RevenueConfigTierInput[];
}

export interface RevenueConfigListResponse {
	success: boolean;
	message: string;
	data: Record<string, RevenueConfiguration[]>;
	timestamp: string;
}

export interface RevenueConfigResponse {
	success: boolean;
	message: string;
	data: RevenueConfiguration[];
	timestamp: string;
}

export interface RevenueConfigDeleteResponse {
	success: boolean;
	message: string;
}

// Helper function for multipart/form-data requests
// Removes Content-Type header to let the browser set it with boundary
const formDataPrepareHeaders = (headers: Headers) => {
	headers.delete('Content-Type');
	return headers;
};

// Create the API endpoints
export const statementAnalyzerApi = paymentStatementApi.injectEndpoints({
	endpoints: (builder) => ({
		uploadStatement: builder.mutation<
			UploadStatementResponse,
			{ formData: FormData; uploadConfig?: UploadConfig }
		>({
			query: ({ formData, uploadConfig }) => {
				// If custom upload config is provided, use it
				if (uploadConfig?.url) {
					return {
						url: uploadConfig.url,
						method: uploadConfig.method || 'POST',
						body: formData,
						prepareHeaders: (headers: Headers) => {
							// Don't set Content-Type for FormData - let browser handle it with boundary
							headers.delete('Content-Type');
							headers.delete('content-type');

							// Add custom token if provided
							if (uploadConfig.token) {
								headers.set('authorization', `Bearer ${uploadConfig.token}`);
							}

							// Add custom headers if provided
							if (uploadConfig.headers) {
								Object.entries(uploadConfig.headers).forEach(([key, value]) => {
									headers.set(key, value);
								});
							}

							return headers;
						},
						// Use custom base URL for this request
						baseUrl: uploadConfig.url.split('/').slice(0, -1).join('/'),
					};
				}

				// Default upload endpoint
				return {
					url: '/statement/upload',
					method: 'POST',
					body: formData,
					prepareHeaders: (headers: Headers) => {
						// Don't set Content-Type for FormData - let browser handle it with boundary
						headers.delete('Content-Type');
						headers.delete('content-type');
						return headers;
					},
				};
			},
			invalidatesTags: ['StatementList'],
			// Add async onQueryStarted to handle post-upload actions
			async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
				try {
					const { data } = await queryFulfilled;
					// After successful upload, invalidate the subscription cache to refetch subscription status
					if (data.file_id) {
						dispatch(
							statementAnalyzerApi.util.invalidateTags(['SubscriptionStatus']),
						);
					}
				} catch {
					// If upload fails, we don't need to invalidate anything
				}
			},
		}),

		getStatements: builder.query<
			StatementListResponse,
			StatementListParams | void
		>({
			query: (params) => {
				const searchParams = new URLSearchParams();
				if (params?.uploaded_by)
					searchParams.append('uploaded_by', params.uploaded_by);
				if (params?.search) searchParams.append('search', params.search);
				if (params?.profit_status)
					searchParams.append('profit_status', params.profit_status);
				if (params?.engine) searchParams.append('engine', params.engine);
				if (params?.page) searchParams.append('page', params.page.toString());
				if (params?.page_size)
					searchParams.append('page_size', params.page_size.toString());

				const queryString = searchParams.toString();
				return `/statement/files${queryString ? `?${queryString}` : ''}`;
			},
			providesTags: ['StatementList'],
		}),

		getEnterpriseUsers: builder.query<EnterpriseUsersResponse, void>({
			query: () => '/statement/files/users',
			providesTags: ['EnterpriseUsers'],
		}),

		getStatementFile: builder.query<StatementFile, string>({
			query: (fileId) => `/statement/file/${fileId}`,
			providesTags: (result, error, fileId) => [
				{ type: 'Statement', id: fileId },
			],
		}),

		getProcessingStatus: builder.query<ProcessingStatus, string>({
			query: (fileId) => `/statement/status/${fileId}`,
			providesTags: (result, error, fileId) => [
				{ type: 'ProcessingStatus', id: fileId },
			],
		}),

		getDetailedDocument: builder.query<DetailedDocument, string>({
			query: (fileId) => `/statement/document/${fileId}/detailed`,
			providesTags: (result, error, fileId) => [
				{ type: 'Statement', id: fileId },
			],
		}),

		getDefaultRates: builder.query<DefaultRatesResponse, void>({
			query: () => '/statement/default-rates',
			providesTags: ['DefaultRates'],
		}),

		getDocumentLogs: builder.query<ProcessingLogs, string>({
			query: (fileId) => `/statement/document/${fileId}/logs`,
			providesTags: (result, error, fileId) => [
				{ type: 'ProcessingStatus', id: fileId },
			],
		}),

		deleteStatement: builder.mutation<DeleteResponse, string>({
			query: (fileId) => ({
				url: `/statement/files/${fileId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['StatementList'],
		}),

		retryStatement: builder.mutation<UploadStatementResponse, string>({
			query: (fileId) => ({
				url: `/statement/files/${fileId}/retry`,
				method: 'POST',
			}),
			invalidatesTags: ['StatementList', 'SubscriptionStatus'],
		}),

		// ROC-90: add the missing pages (append) or the right statement (replace), then rerun.
		addStatementPages: builder.mutation<
			UploadStatementResponse,
			{ fileId: string; files: File[]; mode: 'append' | 'replace' }
		>({
			query: ({ fileId, files, mode }) => {
				const formData = new FormData();
				files.forEach((file) => formData.append('file', file));
				formData.append('mode', mode);
				return {
					url: `/statement/files/${fileId}/pages`,
					method: 'POST',
					body: formData,
				};
			},
			invalidatesTags: (result, error, { fileId }) => [
				'StatementList',
				'SubscriptionStatus',
				{ type: 'Statement', id: fileId },
				{ type: 'ProcessingStatus', id: fileId },
			],
		}),
		rerunStatement: builder.mutation<
			UploadStatementResponse,
			RerunStatementRequest
		>({
			query: ({
				fileId,
				skipCache = true,
				invalidateCache = true,
				deleteResults = true,
				targetRatePlanType,
			}) => ({
				url: `/statement/files/${fileId}/rerun`,
				method: 'POST',
				body: {
					skip_cache: skipCache,
					invalidate_cache: invalidateCache,
					delete_results: deleteResults,
					...(targetRatePlanType
						? { target_rate_plan_type: targetRatePlanType }
						: {}),
				},
				headers: {
					'Content-Type': 'application/json',
				},
			}),
			invalidatesTags: (result, error, { fileId }) => [
				'StatementList',
				'SubscriptionStatus',
				{ type: 'Statement', id: fileId },
			],
		}),

		// Custom Rates endpoints
		createCustomRates: builder.mutation<CustomRatesResponse, CustomRatesConfig>(
			{
				query: (config) => ({
					url: '/statement/custom-rates',
					method: 'POST',
					body: config,
				}),
				transformResponse: (response: any) => response,
				invalidatesTags: ['CustomRates'],
			},
		),

		updateCustomRates: builder.mutation<
			CustomRatesResponse,
			{ id: string; config: CustomRatesConfig }
		>({
			query: ({ id, config }) => ({
				url: `/statement/custom-rates/${id}`,
				method: 'PUT',
				body: config,
			}),
			invalidatesTags: ['CustomRates'],
		}),

		getCustomRates: builder.query<CustomRatesConfig, string>({
			query: (id) => {
				const url = `/statement/custom-rates/${id}`;
				return url;
			},
			transformResponse: (response: any) => {
				if (response && response.data) {
					return response.data;
				}
				// If response is already the data object, return it directly
				if (response && response.id) {
					return response;
				}
				// Fallback
				return response;
			},
			providesTags: (result, error, id) => [{ type: 'CustomRates', id }],
		}),

		getCustomRatesList: builder.query<
			CustomRatesListResponse,
			{ page?: number; page_size?: number }
		>({
			query: ({ page = 1, page_size = 10 }) =>
				`/statement/custom-rates?page=${page}&page_size=${page_size}`,
			transformResponse: (response: any) => response,
			providesTags: ['CustomRates'],
		}),

		deleteCustomRates: builder.mutation<
			{ success: boolean; message: string },
			string
		>({
			query: (id) => ({
				url: `/statement/custom-rates/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['CustomRates'],
		}),

		// Enhanced Custom Rates API Endpoints
		createEnhancedCustomRates: builder.mutation<
			{ success: boolean; message: string; data: any; timestamp: string },
			{
				name: string;
				description?: string;
				is_default: boolean;
				rate_plan_type: 'TIERED' | 'IC_PLUS' | 'FLAT';
				selected_rate_types: ('TIERED' | 'IC_PLUS' | 'FLAT_TIERED')[];
				pricing_mode: {
					discount_rate_mode: 'same_for_all' | 'different_by_card_type';
					authorization_fee_mode:
						| 'same_for_all'
						| 'different_by_transaction_type';
					pass_through_cost_mode: 'standard' | 'custom';
				};
				rates: any;
				pass_through_costs: {
					use_custom_assessment_rates: boolean;
					custom_assessment_rate?: number;
					use_bundled_amex_rate: boolean;
					bundled_amex_rate?: number;
				};
			}
		>({
			query: (config) => ({
				url: '/statement/custom-rates/enhanced',
				method: 'POST',
				body: config,
			}),
			invalidatesTags: ['CustomRates'],
		}),

		updateEnhancedCustomRates: builder.mutation<
			{ success: boolean; message: string; data: any; timestamp: string },
			{
				id: string;
				config: {
					name: string;
					description?: string;
					is_default: boolean;
					rate_plan_type: 'TIERED' | 'IC_PLUS' | 'FLAT' | 'ALL';
					selected_rate_types?: ('TIERED' | 'IC_PLUS' | 'FLAT_TIERED')[];
					pricing_mode: {
						discount_rate_mode: 'same_for_all' | 'different_by_card_type';
						authorization_fee_mode:
							| 'same_for_all'
							| 'different_by_transaction_type';
						pass_through_cost_mode: 'standard' | 'custom';
					};
					rates: any;
					pass_through_costs: {
						use_custom_assessment_rates: boolean;
						custom_assessment_rate?: number;
						use_bundled_amex_rate: boolean;
						bundled_amex_rate?: number;
					};
				};
			}
		>({
			query: ({ id, config }) => ({
				url: `/statement/custom-rates/${id}/enhanced`,
				method: 'PUT',
				body: config,
			}),
			invalidatesTags: ['CustomRates'],
		}),

		getEnhancedCustomRates: builder.query<
			{ success: boolean; message: string; data: any; timestamp: string },
			string
		>({
			query: (id) => ({
				url: `/statement/custom-rates/${id}/enhanced`,
			}),
			providesTags: (result, error, id) => [{ type: 'CustomRates', id }],
		}),

		getPricingAssumptions: builder.query<PricingAssumptionsResponse, string>({
			query: (fileId) => ({
				url: `/statement/files/${fileId}/pricing-assumptions`,
			}),
			providesTags: (result, error, fileId) => [
				{ type: 'Statement', id: fileId },
			],
		}),

		// Feedback endpoints
		submitFeedback: builder.mutation<FeedbackResponse, FeedbackRequest>({
			query: (feedback) => ({
				url: '/statement/feedback',
				method: 'POST',
				body: feedback,
			}),
		}),

		// Subscription status endpoint
		getSubscriptionStatus: builder.query<SubscriptionStatusResponse, void>({
			query: () => '/subscription/status',
			providesTags: ['SubscriptionStatus'],
		}),

		// Subscription API endpoints
		getInvoices: builder.query<InvoicesResponse, void>({
			query: () => '/subscription/invoices',
			providesTags: ['Invoices'],
		}),

		getInvoiceDetails: builder.query<InvoiceDetailsResponse, string>({
			query: (invoiceId) => `/subscription/invoices/${invoiceId}`,
			providesTags: (result, error, invoiceId) => [
				{ type: 'Invoice', id: invoiceId },
			],
		}),

		validatePromoCode: builder.mutation<
			PromoCodeValidationResponse,
			PromoCodeValidationRequest
		>({
			query: (request) => ({
				url: '/subscription/validate-promo-code',
				method: 'POST',
				body: request,
			}),
		}),

		purchaseCredits: builder.mutation<
			PurchaseCreditsResponse,
			PurchaseCreditsRequest
		>({
			query: (request) => ({
				url: '/subscription/purchase-credits',
				method: 'POST',
				body: request,
			}),
			invalidatesTags: ['SubscriptionStatus', 'Invoices'],
		}),

		setupMetered: builder.mutation<SetupMeteredResponse, SetupMeteredRequest>({
			query: (request) => ({
				url: '/subscription/setup-metered',
				method: 'POST',
				body: request,
			}),
			invalidatesTags: ['SubscriptionStatus', 'Invoices'],
		}),

		getBillingConfig: builder.query<BillingConfigResponse, void>({
			query: () => '/subscription/billing-config',
			providesTags: ['BillingConfig'],
		}),

		getPaymentStatus: builder.query<PaymentStatusResponse, string>({
			query: (sessionId) => `/subscription/session-status/${sessionId}`,
			providesTags: (result, error, sessionId) => [
				{ type: 'PaymentStatus', id: sessionId },
			],
		}),

		// Onboarding endpoints
		getOnboardingStatus: builder.query<
			{
				should_show_onboarding: boolean;
				onboarding_completed: boolean;
				onboarding_completed_at?: string;
				enterprise_id: string;
				user_roles: string[];
			},
			void
		>({
			query: () => '/onboarding/status',
			providesTags: ['OnboardingStatus'],
		}),

		completeOnboarding: builder.mutation<
			{ success: boolean; message: string },
			void
		>({
			query: () => ({
				url: '/onboarding/complete',
				method: 'POST',
			}),
			invalidatesTags: ['OnboardingStatus', 'SubscriptionStatus'],
		}),

		resetOnboarding: builder.mutation<
			{ success: boolean; message: string },
			void
		>({
			query: () => ({
				url: '/onboarding/reset',
				method: 'POST',
			}),
			invalidatesTags: ['OnboardingStatus', 'SubscriptionStatus'],
		}),

		// Recalculate statement with custom rates
		recalculateStatement: builder.mutation<
			{
				success: boolean;
				message: string;
				// Set when the edit broke a pricing limit and was sent for approval (ROC-87).
				approval_request?: { id: string; status: string };
			},
			{
				fileId: string;
				config: {
					direct_rates?: {
						rate_plan_type: string;
						selected_rate_types?: string[];
						pricing_mode: {
							discount_rate_mode: string;
							authorization_fee_mode: string;
							pass_through_cost_mode: string;
						};
						rates: any;
						pass_through_costs: {
							use_custom_assessment_rates: boolean;
							custom_assessment_rate?: number;
							use_bundled_amex_rate: boolean;
							bundled_amex_rate?: number;
						};
						monthly_and_batch_fees?: MonthlyAndBatchFeesConfig;
					};
					rerun?: boolean;
					assumptions?: PricingAssumptions | null;
					selected_risk_tier?: string; // For Coastal Pay ISO profit analysis (LOW, MEDIUM, HIGH, OTHER)
					approval_reason?: string; // ROC-87: why pricing outside the limits is needed
				};
			}
		>({
			query: ({ fileId, config }) => ({
				url: `/statement/files/${fileId}/recalculate`,
				method: 'POST',
				body: config,
				headers: {
					'Content-Type': 'application/json',
				},
			}),
			invalidatesTags: (result, error, { fileId }) => [
				'StatementList',
				{ type: 'Statement', id: fileId },
				// Invalidate the line-items cache so getLineItems refetches after a
				// recalculate — that response carries the recomputed revenue_analysis
				// (ISO Profit). Without this the refresh relied solely on the
				// imperative handlePostRecalculation callback (no RTK safety net).
				{ type: 'LineItems' as const, id: `${fileId}_default` },
				{ type: 'LineItems' as const, id: `${fileId}_user_edits` },
				{ type: 'LineItems' as const, id: `${fileId}_customized_quote` },
				'PricingApprovals',
			],
		}),

		// ROC-114: the dock's discount rate. Re-rates the discount lines in place,
		// so line edits, moves and feedback survive (recalculate rebuilds them).
		setDiscountRate: builder.mutation<
			{ success: boolean; lines_changed: number },
			{ fileId: string; directRates: Record<string, unknown> }
		>({
			query: ({ fileId, directRates }) => ({
				url: `/statement/files/${fileId}/discount-rate`,
				method: 'PUT',
				body: { direct_rates: directRates },
			}),
			invalidatesTags: (result, error, { fileId }) => [
				{ type: 'Statement', id: fileId },
				{ type: 'LineItems' as const, id: `${fileId}_default` },
				{ type: 'LineItems' as const, id: `${fileId}_user_edits` },
				{ type: 'LineItems' as const, id: `${fileId}_customized_quote` },
			],
		}),

		exportPDF: builder.mutation<
			Blob,
			{
				fileId: string;
				filename?: string;
				view_type?: 'summary' | 'full';
				view_mode?: 'default' | 'user_edits' | 'customized_quote';
			}
		>({
			query: ({ fileId, filename, view_type, view_mode }) => ({
				url: '/statement/export/pdf',
				method: 'POST',
				body: {
					file_id: fileId,
					filename: filename,
					view_type: view_type || 'summary',
					view_mode: view_mode || 'default',
				},
				responseHandler: async (response) => {
					if (!response.ok) {
						const error = await response.json();
						throw new Error(error.message || 'Failed to export PDF');
					}
					return response.blob();
				},
			}),
		}),

		// Enterprise Settings API
		getEnterpriseSettings: builder.query<EnterpriseSettingsResponse, void>({
			query: () => '/enterprise-settings',
			providesTags: ['EnterpriseSettings'],
		}),

		createEnterpriseSettings: builder.mutation<
			EnterpriseSettingsResponse,
			FormData
		>({
			query: (formData) => ({
				url: '/enterprise-settings',
				method: 'POST',
				body: formData,
				prepareHeaders: formDataPrepareHeaders,
			}),
			invalidatesTags: ['EnterpriseSettings'],
		}),

		updateEnterpriseSettings: builder.mutation<
			EnterpriseSettingsResponse,
			FormData
		>({
			query: (formData) => ({
				url: '/enterprise-settings',
				method: 'PATCH',
				body: formData,
				prepareHeaders: formDataPrepareHeaders,
			}),
			invalidatesTags: ['EnterpriseSettings'],
		}),

		// Revenue Configuration API
		getRevenueConfigurations: builder.query<RevenueConfigListResponse, void>({
			query: () => '/revenue-configs',
			transformResponse: (response: any) => {
				// Transform the API response to match the expected format
				// API returns: { data: [{ name, tiers: { LOW, MEDIUM, HIGH, OTHER? } }] }
				// We need: { data: { name: [{ risk_tier: LOW }, { risk_tier: MEDIUM }, { risk_tier: HIGH }, { risk_tier: OTHER }?] } }
				const groupedData: Record<string, RevenueConfiguration[]> = {};

				if (response.data && Array.isArray(response.data)) {
					response.data.forEach((item: any) => {
						const tierConfigs: RevenueConfiguration[] = [];

						// Convert tiers object to array of configurations
						if (item.tiers) {
							['LOW', 'MEDIUM', 'HIGH', 'OTHER'].forEach((tier) => {
								if (item.tiers[tier]) {
									const tierData = item.tiers[tier];
									tierConfigs.push({
										id: tierData.id,
										enterprise_id: item.enterprise_id,
										name: item.name,
										description: item.description,
										is_active: item.is_active,
										risk_tier: tier as RiskTier,
										partner_revenue_share_percent:
											item.partner_revenue_share_percent,
										// Legacy rates
										discount_buy_rate: tierData.discount_buy_rate,
										authorization_buy_rate: tierData.authorization_buy_rate,
										// NEW: Split discount rates
										discount_buy_rate_vmd: tierData.discount_buy_rate_vmd,
										discount_buy_rate_amex: tierData.discount_buy_rate_amex,
										// NEW: Split auth fees
										auth_buy_rate_standard: tierData.auth_buy_rate_standard,
										auth_buy_rate_pin_debit: tierData.auth_buy_rate_pin_debit,
										auth_buy_rate_ebt: tierData.auth_buy_rate_ebt,
										// NEW: Batch fee
										batch_fee_rate: tierData.batch_fee_rate,
										// NEW: Dynamic monthly fees
										monthly_fee_costs: tierData.monthly_fee_costs || [],
										// Legacy monthly fees
										monthly_fee_cost_1: tierData.monthly_fee_cost_1,
										monthly_fee_cost_1_label: tierData.monthly_fee_cost_1_label,
										monthly_fee_cost_2: tierData.monthly_fee_cost_2,
										monthly_fee_cost_2_label: tierData.monthly_fee_cost_2_label,
										monthly_fee_cost_3: tierData.monthly_fee_cost_3,
										monthly_fee_cost_3_label: tierData.monthly_fee_cost_3_label,
										agent_revenue_share_percent:
											tierData.agent_revenue_share_percent,
										created_by: item.created_by,
										updated_by: item.updated_by,
										created_at: item.created_at,
										updated_at: item.updated_at,
									});
								}
							});
						}

						groupedData[item.name] = tierConfigs;
					});
				}

				return {
					success: response.success,
					message: response.message || '',
					data: groupedData,
					timestamp: response.timestamp || new Date().toISOString(),
				};
			},
			providesTags: ['RevenueConfigs'],
		}),

		getRevenueConfigurationById: builder.query<RevenueConfigResponse, string>({
			query: (id) => `/revenue-configs/${id}`,
			providesTags: (result, error, id) => [{ type: 'RevenueConfigs', id }],
		}),

		getRevenueConfigurationByName: builder.query<RevenueConfigResponse, string>(
			{
				query: (name) => `/revenue-configs/by-name/${name}`,
				providesTags: (result, error, name) => [
					{ type: 'RevenueConfigs', id: name },
				],
			},
		),

		getActiveRevenueConfiguration: builder.query<
			{
				success: boolean;
				message: string;
				data: RevenueConfiguration;
				timestamp: string;
			},
			RiskTier
		>({
			query: (tier) => `/revenue-configs/active/${tier}`,
			providesTags: (result, error, tier) => [
				{ type: 'RevenueConfigs', id: `active-${tier}` },
			],
		}),

		createRevenueConfiguration: builder.mutation<
			RevenueConfigResponse,
			CreateRevenueConfigRequest
		>({
			query: (config) => ({
				url: '/revenue-configs',
				method: 'POST',
				body: config,
			}),
			invalidatesTags: ['RevenueConfigs'],
		}),

		updateRevenueConfiguration: builder.mutation<
			RevenueConfigResponse,
			{ id: string; config: UpdateRevenueConfigRequest }
		>({
			query: ({ id, config }) => ({
				url: `/revenue-configs/${id}`,
				method: 'PUT',
				body: config,
			}),
			invalidatesTags: ['RevenueConfigs'],
		}),

		deleteRevenueConfiguration: builder.mutation<
			RevenueConfigDeleteResponse,
			string
		>({
			query: (id) => ({
				url: `/revenue-configs/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['RevenueConfigs'],
		}),

		// ============================================================================
		// Line Item API Endpoints
		// ============================================================================

		// Get line items for a statement file
		// This is the SINGLE SOURCE OF TRUTH for rendering line items
		// Do NOT use processing_results from detailed API - always use this endpoint
		getLineItems: builder.query<
			LineItemsListResponse,
			{ fileId: string; viewMode?: string }
		>({
			query: ({ fileId, viewMode = 'default' }) => ({
				url: `/statement/files/${fileId}/line-items`,
				params: { view_mode: viewMode },
			}),
			providesTags: (result, error, arg) => [
				{ type: 'LineItems', id: `${arg.fileId}_${arg.viewMode || 'default'}` },
				...(result?.line_items?.map((item) => ({
					type: 'LineItem' as const,
					id: item.id,
				})) || []),
			],
		}),

		// Get a single line item by ID
		getLineItem: builder.query<LineItemDetailResponse, string>({
			query: (lineItemId) => `/statement/line-items/${lineItemId}`,
			providesTags: (result, error, lineItemId) => [
				{ type: 'LineItem', id: lineItemId },
			],
		}),

		// ROC-105: confirm flagged figures; no ids confirms all of them
		confirmLineItems: builder.mutation<
			{ success: boolean; confirmed: number; remaining: number },
			{ fileId: string; lineItemIds?: string[] }
		>({
			query: ({ fileId, lineItemIds }) => ({
				url: `/statement/files/${fileId}/line-item-confirmations`,
				method: 'POST',
				body: lineItemIds ? { line_item_ids: lineItemIds } : {},
			}),
			invalidatesTags: (result, error, { fileId }) => [
				{ type: 'LineItems' as const, id: `${fileId}_default` },
				{ type: 'LineItems' as const, id: `${fileId}_user_edits` },
				{ type: 'LineItems' as const, id: `${fileId}_customized_quote` },
			],
		}),

		// Update a line item
		updateLineItem: builder.mutation<
			LineItemUpdateResponse,
			{ lineItemId: string; fileId?: string; updates: LineItemUpdateRequest }
		>({
			query: ({ lineItemId, updates }) => ({
				url: `/statement/line-items/${lineItemId}`,
				method: 'PUT',
				body: updates,
			}),
			// Invalidate the file's line items list to trigger automatic refetch
			// This ensures RTK Query automatically refetches getLineItems when tags are invalidated
			// Do NOT invalidate 'Statement' or 'DetailedDocument' tags to avoid triggering detailed API
			invalidatesTags: (result, error, { lineItemId, fileId }) => {
				if (fileId) {
					// Invalidate the specific line item and all view-mode variants of the line items list.
					// getLineItems tags include viewMode suffix (e.g. '{fileId}_default'), so we must
					// invalidate all variants to ensure RTK Query refetches regardless of active view.
					return [
						{ type: 'LineItem' as const, id: lineItemId },
						{ type: 'LineItems' as const, id: `${fileId}_default` },
						{ type: 'LineItems' as const, id: `${fileId}_user_edits` },
						{ type: 'LineItems' as const, id: `${fileId}_customized_quote` },
					];
				}
				// If no fileId, only invalidate the specific line item
				return [{ type: 'LineItem' as const, id: lineItemId }];
			},
		}),

		// Get audit logs for a line item
		getLineItemAuditLogs: builder.query<LineItemAuditLogsResponse, string>({
			query: (lineItemId) => `/statement/line-items/${lineItemId}/audit-logs`,
			providesTags: (result, error, lineItemId) => [
				{ type: 'LineItemAuditLogs', id: lineItemId },
			],
		}),

		// Bulk update line items (supports both simplified move API and legacy updates)
		bulkUpdateLineItems: builder.mutation<
			BulkLineItemUpdateResponse,
			{ updates: BulkLineItemUpdate[]; fileId?: string }
		>({
			query: ({ updates }) => {
				// Clean updates: convert empty strings to null, remove undefined values
				const cleanedUpdates = updates.map((update) => {
					const cleaned: BulkLineItemUpdate = {};

					// Handle simplified move API
					if (update.current_item_id) {
						cleaned.current_item_id = update.current_item_id;
						if (update.target_item_id !== undefined) {
							cleaned.target_item_id =
								update.target_item_id === '' ? null : update.target_item_id;
						}
						if (update.target_section !== undefined) {
							cleaned.target_section = update.target_section;
						}
					} else {
						// Legacy update API
						if (update.line_item_id) {
							cleaned.line_item_id = update.line_item_id;
						}
						// Only include parent_id if it's explicitly set (null or valid string)
						if (update.parent_id !== undefined) {
							cleaned.parent_id =
								update.parent_id === '' ? null : update.parent_id;
						}
						// Only include section if set
						if (update.section !== undefined) {
							cleaned.section = update.section;
						}
					}

					// Include other fields only if defined
					if (update.description !== undefined)
						cleaned.description = update.description;
					if (update.amount !== undefined) cleaned.amount = update.amount;
					if (update.fee_type_id !== undefined)
						cleaned.fee_type_id = update.fee_type_id;
					if (update.processor_id !== undefined)
						cleaned.processor_id = update.processor_id;
					if (update.network_id !== undefined)
						cleaned.network_id = update.network_id;
					if (update.rate !== undefined) cleaned.rate = update.rate;
					if (update.per_transaction_fee !== undefined)
						cleaned.per_transaction_fee = update.per_transaction_fee;
					if (update.volume !== undefined) cleaned.volume = update.volume;
					if (update.transaction_count !== undefined)
						cleaned.transaction_count = update.transaction_count;
					if (update.display_order !== undefined)
						cleaned.display_order = update.display_order;

					return cleaned;
				});

				return {
					url: `/statement/line-items/bulk-update`,
					method: 'PUT',
					body: { updates: cleanedUpdates },
				};
			},
			invalidatesTags: (result, error, { fileId }) => {
				if (fileId) {
					// Invalidate all view-mode variants of the line items list.
					// getLineItems tags include viewMode suffix, so we must match all variants.
					return [
						{ type: 'LineItems' as const, id: `${fileId}_default` },
						{ type: 'LineItems' as const, id: `${fileId}_user_edits` },
						{ type: 'LineItems' as const, id: `${fileId}_customized_quote` },
					];
				}
				// Fallback if no fileId is provided, though it's recommended
				return ['LineItems'];
			},
		}),

		// ============================================================================
		// Dimension List Endpoints (Fee Types, Processors, Networks)
		// ============================================================================

		// Get all fee types
		getFeeTypes: builder.query<FeeTypesListResponse, void>({
			query: () => '/statement/fee-types',
			providesTags: ['FeeTypes'],
		}),

		// Get all processors
		getProcessors: builder.query<ProcessorsListResponse, void>({
			query: () => '/statement/processors',
			providesTags: ['Processors'],
		}),

		// Get all networks
		getNetworks: builder.query<NetworksListResponse, void>({
			query: () => '/statement/networks',
			providesTags: ['Networks'],
		}),
	}),
	overrideExisting: false,
});

// Export hooks
export const {
	useUploadStatementMutation,
	useGetStatementsQuery,
	useGetEnterpriseUsersQuery,
	useGetStatementFileQuery,
	useGetProcessingStatusQuery,
	useGetDetailedDocumentQuery,
	useGetDocumentLogsQuery,
	useDeleteStatementMutation,
	useRetryStatementMutation,
	useRerunStatementMutation,
	useAddStatementPagesMutation,
	useCreateCustomRatesMutation,
	useUpdateCustomRatesMutation,
	useGetCustomRatesQuery,
	useGetCustomRatesListQuery,
	useDeleteCustomRatesMutation,
	// Enhanced Custom Rates API hooks
	useCreateEnhancedCustomRatesMutation,
	useUpdateEnhancedCustomRatesMutation,
	useGetEnhancedCustomRatesQuery,
	useGetPricingAssumptionsQuery,
	useSubmitFeedbackMutation,
	useGetSubscriptionStatusQuery,
	useGetDefaultRatesQuery,
	useGetOnboardingStatusQuery,
	useCompleteOnboardingMutation,
	useResetOnboardingMutation,
	useRecalculateStatementMutation,
	useSetDiscountRateMutation,
	// Subscription API hooks
	useGetInvoicesQuery,
	useGetInvoiceDetailsQuery,
	useValidatePromoCodeMutation,
	usePurchaseCreditsMutation,
	useSetupMeteredMutation,
	useGetBillingConfigQuery,
	useGetPaymentStatusQuery,
	// PDF Export API hook
	useExportPDFMutation,
	// Enterprise Settings API hooks
	useGetEnterpriseSettingsQuery,
	useCreateEnterpriseSettingsMutation,
	useUpdateEnterpriseSettingsMutation,
	// Revenue Configuration API hooks
	useGetRevenueConfigurationsQuery,
	useGetRevenueConfigurationByIdQuery,
	useGetRevenueConfigurationByNameQuery,
	useGetActiveRevenueConfigurationQuery,
	useCreateRevenueConfigurationMutation,
	useUpdateRevenueConfigurationMutation,
	useDeleteRevenueConfigurationMutation,
	// Line Item API hooks
	useGetLineItemsQuery,
	useGetLineItemQuery,
	useUpdateLineItemMutation,
	useConfirmLineItemsMutation,
	useBulkUpdateLineItemsMutation,
	useGetLineItemAuditLogsQuery,
	// Dimension List API hooks
	useGetFeeTypesQuery,
	useGetProcessorsQuery,
	useGetNetworksQuery,
} = statementAnalyzerApi;
